/*
 * buylocally-charts.js
 * Renders "Purchase Locally" materials charts from a published Google Sheet.
 * Each chart container points at a Sheet ID + a tab name; this script fetches
 * that tab as CSV and builds the table. Header-driven, so it adapts to whatever
 * columns each tab has (multi-length kits, the single-qty hobby kit, etc.).
 *
 * Sheet column convention:
 *   - First column "Description" marks a header row (starts a table).
 *   - A row with only the first cell filled is a TITLE if a header follows it,
 *     otherwise a NOTE (rendered full width, e.g. "*** Optional items").
 *   - A row whose first cell contains "Total" is a totals row.
 *   - A "<X> Part" column is hyperlinked using the sibling "<X> Link" column;
 *     "* Link" columns are not shown as their own column.
 */
(function () {
  var W = typeof window !== 'undefined' ? window : {};
  if (W.__buylocallyChartsInit) return;
  W.__buylocallyChartsInit = true;

  var CSS = '' +
    '.buylocally-chart{margin:1.5em 0;overflow-x:auto}' +
    '.buylocally-chart h3{margin:1em 0 .4em}' +
    '.buylocally-chart table{border-collapse:collapse;width:100%;font-size:.92em}' +
    '.buylocally-chart th,.buylocally-chart td{border:1px solid #ccc;padding:6px 9px;text-align:left;vertical-align:top}' +
    '.buylocally-chart table thead th{background-color:#2f6f4e !important;color:#fff !important;font-weight:600}' +
    '.buylocally-chart tr.is-total td{font-weight:700;background:#f3f7f4}' +
    '.buylocally-chart tr.is-note td{font-style:italic;background:#fafafa}' +
    '.buylocally-chart a{color:#1a6fb5;text-decoration:underline}' +
    '.buylocally-chart__status,.buylocally-chart__error{padding:.5em 0;color:#777;font-style:italic}' +
    '.buylocally-chart__error{color:#b00}';

  function injectCSS() {
    if (document.getElementById('buylocally-charts-css')) return;
    var s = document.createElement('style');
    s.id = 'buylocally-charts-css';
    s.textContent = CSS;
    document.head.appendChild(s);
  }

  // --- Minimal robust CSV parser (handles quotes, commas, CRLF, "" escapes) ---
  function parseCSV(text) {
    var rows = [], row = [], field = '', inQ = false, i = 0, c, n;
    text = text.replace(/^﻿/, '');
    while (i < text.length) {
      c = text[i];
      if (inQ) {
        if (c === '"') {
          if (text[i + 1] === '"') { field += '"'; i += 2; continue; }
          inQ = false; i++; continue;
        }
        field += c; i++; continue;
      }
      if (c === '"') { inQ = true; i++; continue; }
      if (c === ',') { row.push(field); field = ''; i++; continue; }
      if (c === '\r') { i++; continue; }
      if (c === '\n') { row.push(field); rows.push(row); row = []; field = ''; i++; continue; }
      field += c; i++;
    }
    if (field.length || row.length) { row.push(field); rows.push(row); }
    return rows;
  }

  function esc(s) {
    return String(s).replace(/[&<>"]/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch];
    });
  }
  // Format a price-like cell as US currency ($#,##0.00). Leaves blanks blank
  // and non-numeric text untouched (e.g. "Approx. Total Cost").
  function toCurrency(v) {
    v = (v == null ? '' : String(v)).trim();
    if (v === '') return '';
    var n = parseFloat(v.replace(/[$,\s]/g, ''));
    if (isNaN(n)) return v;
    return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  function firstCell(r) { return (r[0] || '').trim(); }
  function isBlank(r) { return r.every(function (c) { return (c || '').trim() === ''; }); }
  function isHeader(r) { return firstCell(r).toLowerCase() === 'description'; }
  function isTotal(r) { return /total/i.test(firstCell(r)); }
  function onlyFirst(r) {
    return firstCell(r) !== '' && r.slice(1).every(function (c) { return (c || '').trim() === ''; });
  }
  function nextNonBlank(rows, from) {
    for (var j = from; j < rows.length; j++) { if (!isBlank(rows[j])) return rows[j]; }
    return null;
  }

  function buildHTML(rows) {
    var out = [], cols = null, open = false;

    function closeTable() { if (open) { out.push('</tbody></table>'); open = false; } }

    // Column meanings are locked to fixed positions so a blank/edited header cell
    // can never silently drop a column (prices/parts always render). The header row
    // supplies display LABELS; positions supply MEANING. Layout:
    //   0 Description | 1 HD Part | 2 HD Link | 3 Lowes Part | 4 Lowes Link |
    //   5 HD Price | 6 Lowes Price | 7+ quantities (multi-length) OR 7 Qty, 8 Cost (single)
    var DEFAULT_LABELS = { 0: 'Description', 1: 'HD Part', 3: 'Lowes Part', 5: 'HD Price', 6: 'Lowes Price' };

    function startTable(headerRow) {
      closeTable();
      cols = [];
      var n = headerRow.length;
      var single = n <= 9; // door/hobby kits use Qty + Cost instead of length columns
      for (var idx = 0; idx < n; idx++) {
        var raw = (headerRow[idx] || '').trim();
        if (idx === 2 || idx === 4 || /link$/i.test(raw)) continue; // link columns are hidden
        var label = raw || DEFAULT_LABELS[idx] || (single && idx === 7 ? 'Qty' : (single && idx === 8 ? 'Cost' : ''));
        var isPart = idx === 1 || idx === 3 || /part$/i.test(raw);
        cols.push({
          idx: idx,
          label: label,
          linkIdx: isPart ? idx + 1 : null,                                        // part # links via the next column
          cur: idx === 5 || idx === 6 || /price$/i.test(raw) || /^cost$/i.test(raw) || (single && idx === 8)
        });
      }
      out.push('<table><thead><tr>');
      cols.forEach(function (c) { out.push('<th>' + esc(c.label) + '</th>'); });
      out.push('</tr></thead><tbody>');
      open = true;
    }

    function cell(row, c, isTotalRow, pos) {
      var val = (row[c.idx] || '').trim();
      if (c.linkIdx != null) {
        var href = (row[c.linkIdx] || '').trim();
        if (href) return '<a href="' + esc(href) + '" target="_blank" rel="noopener">' + esc(val) + '</a>';
        return esc(val);
      }
      // Price/Cost columns always render as currency. In a totals row, the dollar
      // figures live in the quantity columns, so format every value cell but the label.
      if (c.cur || (isTotalRow && pos > 0)) return esc(toCurrency(val));
      return esc(val);
    }

    function dataRow(row, cls, isTotalRow) {
      out.push('<tr' + (cls ? ' class="' + cls + '"' : '') + '>');
      cols.forEach(function (c, p) { out.push('<td>' + cell(row, c, isTotalRow, p) + '</td>'); });
      out.push('</tr>');
    }

    for (var i = 0; i < rows.length; i++) {
      var r = rows[i];
      if (isBlank(r)) continue;
      if (isHeader(r)) { startTable(r); continue; }
      if (onlyFirst(r)) {
        var nxt = nextNonBlank(rows, i + 1);
        if (nxt && isHeader(nxt)) { closeTable(); out.push('<h3>' + esc(firstCell(r)) + '</h3>'); }
        else if (open) { out.push('<tr class="is-note"><td colspan="' + cols.length + '">' + esc(firstCell(r)) + '</td></tr>'); }
        continue;
      }
      if (!open) continue; // data before any header — skip defensively
      var total = isTotal(r);
      dataRow(r, total ? 'is-total' : '', total);
    }
    closeTable();
    return out.join('');
  }

  function render(container) {
    var sheetId = (container.getAttribute('data-sheet-id') || '').trim();
    var tab = (container.getAttribute('data-buylocally-chart') || '').trim();
    if (!sheetId || sheetId.indexOf('PASTE_') === 0) {
      container.innerHTML = '<p class="buylocally-chart__error">Chart sheet not configured yet.</p>';
      return;
    }
    var url = 'https://docs.google.com/spreadsheets/d/' + encodeURIComponent(sheetId) +
              '/gviz/tq?tqx=out:csv&headers=0&sheet=' + encodeURIComponent(tab);
    fetch(url)
      .then(function (res) { if (!res.ok) throw new Error('HTTP ' + res.status); return res.text(); })
      .then(function (csv) {
        var html = buildHTML(parseCSV(csv));
        container.innerHTML = html || '<p class="buylocally-chart__error">No data found for "' + esc(tab) + '".</p>';
      })
      .catch(function (err) {
        container.innerHTML = '<p class="buylocally-chart__error">Materials list is temporarily unavailable.</p>';
        if (window.console) console.error('[buylocally-chart] failed for tab "' + tab + '"', err);
      });
  }

  function init() {
    injectCSS();
    var nodes = document.querySelectorAll('[data-buylocally-chart]');
    Array.prototype.forEach.call(nodes, function (el) {
      if (el.getAttribute('data-buylocally-done')) return;
      el.setAttribute('data-buylocally-done', '1');
      render(el);
    });
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { parseCSV: parseCSV, buildHTML: buildHTML };
    return;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
